/**
 * @fileoverview Audit log query model (MOR-1012) — the period, filter and
 * column state the query builder edits and the panel applies to the index.
 * Kept apart from the builder component so that file exports only
 * components (Fast Refresh).
 */

// ---------------------------------------------------------------------------
// Query model
// ---------------------------------------------------------------------------

export type AuditPeriod =
  "all" | "today" | "7d" | "30d" | "this_month" | "this_quarter";

export const AUDIT_PERIOD_LABELS: Record<AuditPeriod, string> = {
  all: "All time",
  today: "Today",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  this_month: "This month",
  this_quarter: "This quarter",
};

/** Start timestamp for a period, or null for no lower bound. */
export function periodStart(
  period: AuditPeriod,
  now = new Date(),
): number | null {
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (period) {
    case "all":
      return null;
    case "today":
      return startOfDay.getTime();
    case "7d":
      return startOfDay.getTime() - 6 * 86_400_000;
    case "30d":
      return startOfDay.getTime() - 29 * 86_400_000;
    case "this_month":
      return new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    case "this_quarter":
      return new Date(
        now.getFullYear(),
        Math.floor(now.getMonth() / 3) * 3,
        1,
      ).getTime();
  }
}

export type AuditFilterField =
  "location" | "actorType" | "actorName" | "action" | "recordLabel";

export const AUDIT_FILTER_FIELD_LABELS: Record<AuditFilterField, string> = {
  location: "Change location",
  actorType: "Actor type",
  actorName: "User / Agent",
  action: "Action",
  recordLabel: "Record",
};

export type AuditCondition = {
  field: AuditFilterField;
  negate: boolean;
  values: string[];
};

export type AuditColumn =
  "date" | "location" | "actor" | "description" | "change";

export const AUDIT_COLUMN_LABELS: Record<AuditColumn, string> = {
  date: "Date",
  location: "Change location",
  actor: "User / Agent",
  description: "Description",
  change: "Change",
};

export const ALL_AUDIT_COLUMNS: ReadonlyArray<AuditColumn> = [
  "date",
  "location",
  "actor",
  "description",
  "change",
];

export type AuditQuery = {
  period: AuditPeriod;
  conditions: AuditCondition[];
  columns: AuditColumn[];
};

export const DEFAULT_AUDIT_QUERY: AuditQuery = {
  period: "all",
  conditions: [],
  columns: [...ALL_AUDIT_COLUMNS],
};

export type AuditOption = { value: string; label: string };
export type AuditDimension = {
  field: AuditFilterField;
  label: string;
  options: ReadonlyArray<AuditOption>;
};

/** Apply one condition to a row's value for its field. */
export function conditionMatches(
  condition: AuditCondition,
  value: string | undefined,
): boolean {
  const hit = value !== undefined && condition.values.includes(value);
  return condition.negate ? !hit : hit;
}
